import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { BellRing, Camera, Check, KeyRound, Mail, MonitorSmartphone, Trash2, UserRound } from "lucide-react";
import { useAuth } from "@/components/auth-context";
import { Button, Field, Input, LoadingLine, PageHeader } from "@/components/primitives";
import { apiFetch } from "@/lib/api";

interface Notice {
  tone: "success" | "error";
  text: string;
}

interface StudyReminderPreference {
  enabled: boolean;
  time: string;
  timezone: "Asia/Shanghai";
  lastSentAt: string | null;
}

const ERROR_MESSAGES: Record<string, string> = {
  invalid_profile: "昵称格式不正确。",
  current_password_incorrect: "当前密码不正确。",
  invalid_password: "新密码至少需要 10 位，并且不能与当前密码相同。",
  email_unchanged: "新邮箱与当前邮箱相同。",
  email_in_use: "该邮箱已被其他账户使用。",
  invalid_email_change: "请检查邮箱和密码。",
  invalid_code: "验证码不正确或已过期。",
  cooldown: "发送过于频繁，请稍后再试。",
  email_unavailable: "验证邮件暂时无法发送，请稍后重试。",
  email_not_verified: "请先完成登录邮箱验证，再开启学习提醒。",
  invalid_study_reminder: "提醒时间格式不正确。",
  unsupported_avatar: "请选择 JPEG、PNG 或 WebP 图片。",
  avatar_too_large: "头像文件不能超过 5 MB。",
  avatar_dimensions_invalid: "头像尺寸至少需要 64 × 64 像素。",
  invalid_avatar: "图片无法读取，请换一张重试。",
};

function messageFor(error: unknown) {
  const code = error instanceof Error ? error.message : "request_failed";
  return ERROR_MESSAGES[code] ?? "暂时无法完成操作，请稍后重试。";
}

function StatusMessage({ notice }: { notice: Notice | null }) {
  return notice ? <p className="form-status" data-tone={notice.tone} role="status">{notice.text}</p> : null;
}

export function ProfilePage() {
  const { user, refresh } = useAuth();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [profileNotice, setProfileNotice] = useState<Notice | null>(null);
  const [profileBusy, setProfileBusy] = useState(false);
  const [avatarNotice, setAvatarNotice] = useState<Notice | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const publicConfig = useQuery({
    queryKey: ["public", "config"],
    queryFn: () => apiFetch<{ uploadLimits: { avatarMaxMb: number } }>("/public/config"),
    staleTime: 60_000,
  });
  const avatarMaxMb = publicConfig.data?.uploadLimits.avatarMaxMb ?? 5;

  const [emailStep, setEmailStep] = useState<"request" | "confirm">("request");
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailNotice, setEmailNotice] = useState<Notice | null>(null);
  const [emailBusy, setEmailBusy] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [nextPassword, setNextPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordNotice, setPasswordNotice] = useState<Notice | null>(null);
  const [passwordBusy, setPasswordBusy] = useState(false);

  const [sessionNotice, setSessionNotice] = useState<Notice | null>(null);
  const [sessionBusy, setSessionBusy] = useState(false);

  const reminderQuery = useQuery({
    queryKey: ["account", "study-reminder"],
    queryFn: () => apiFetch<StudyReminderPreference>("/account/study-reminder"),
  });
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderTime, setReminderTime] = useState("20:00");
  const [reminderLastSentAt, setReminderLastSentAt] = useState<string | null>(null);
  const [reminderNotice, setReminderNotice] = useState<Notice | null>(null);
  const [reminderBusy, setReminderBusy] = useState(false);

  useEffect(() => {
    setDisplayName(user?.displayName ?? "");
  }, [user?.displayName]);

  useEffect(() => {
    if (!reminderQuery.data) return;
    setReminderEnabled(reminderQuery.data.enabled);
    setReminderTime(reminderQuery.data.time);
    setReminderLastSentAt(reminderQuery.data.lastSentAt);
  }, [reminderQuery.data]);

  if (!user) return null;

  const saveProfile = async (event: FormEvent) => {
    event.preventDefault();
    setProfileBusy(true);
    setProfileNotice(null);
    try {
      await apiFetch("/account/profile", {
        method: "PUT",
        body: JSON.stringify({ displayName }),
      });
      await refresh();
      setProfileNotice({ tone: "success", text: "昵称已保存。" });
    } catch (error) {
      setProfileNotice({ tone: "error", text: messageFor(error) });
    } finally {
      setProfileBusy(false);
    }
  };

  const uploadAvatar = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > avatarMaxMb * 1024 * 1024) {
      setAvatarNotice({ tone: "error", text: `头像文件不能超过 ${avatarMaxMb} MB。` });
      return;
    }
    setAvatarBusy(true);
    setAvatarNotice(null);
    try {
      const body = new FormData();
      body.append("file", file);
      await apiFetch("/account/avatar", { method: "POST", body });
      await refresh();
      setAvatarNotice({ tone: "success", text: "头像已更新。" });
    } catch (error) {
      setAvatarNotice({ tone: "error", text: messageFor(error) });
    } finally {
      setAvatarBusy(false);
    }
  };

  const removeAvatar = async () => {
    setAvatarBusy(true);
    setAvatarNotice(null);
    try {
      await apiFetch("/account/avatar", { method: "DELETE" });
      await refresh();
      setAvatarNotice({ tone: "success", text: "已恢复默认头像。" });
    } catch (error) {
      setAvatarNotice({ tone: "error", text: messageFor(error) });
    } finally {
      setAvatarBusy(false);
    }
  };

  const requestEmailChange = async (event: FormEvent) => {
    event.preventDefault();
    setEmailBusy(true);
    setEmailNotice(null);
    try {
      await apiFetch("/account/email-change/request", {
        method: "POST",
        body: JSON.stringify({ email: newEmail, currentPassword: emailPassword }),
      });
      setEmailPassword("");
      setEmailStep("confirm");
      setEmailNotice({ tone: "success", text: "验证码已发送到新邮箱。" });
    } catch (error) {
      setEmailNotice({ tone: "error", text: messageFor(error) });
    } finally {
      setEmailBusy(false);
    }
  };

  const confirmEmailChange = async (event: FormEvent) => {
    event.preventDefault();
    setEmailBusy(true);
    setEmailNotice(null);
    try {
      await apiFetch("/account/email-change/confirm", {
        method: "POST",
        body: JSON.stringify({ email: newEmail, code: emailCode }),
      });
      await refresh();
      setEmailStep("request");
      setNewEmail("");
      setEmailCode("");
      setEmailNotice({ tone: "success", text: "登录邮箱已更新，其他设备已退出登录。" });
    } catch (error) {
      setEmailNotice({ tone: "error", text: messageFor(error) });
    } finally {
      setEmailBusy(false);
    }
  };

  const changePassword = async (event: FormEvent) => {
    event.preventDefault();
    setPasswordNotice(null);
    if (nextPassword !== confirmPassword) {
      setPasswordNotice({ tone: "error", text: "两次输入的新密码不一致。" });
      return;
    }
    setPasswordBusy(true);
    try {
      await apiFetch("/account/password", {
        method: "PUT",
        body: JSON.stringify({ currentPassword, newPassword: nextPassword }),
      });
      setCurrentPassword("");
      setNextPassword("");
      setConfirmPassword("");
      setPasswordNotice({ tone: "success", text: "密码已更新，其他设备已退出登录。" });
    } catch (error) {
      setPasswordNotice({ tone: "error", text: messageFor(error) });
    } finally {
      setPasswordBusy(false);
    }
  };

  const revokeOtherSessions = async () => {
    setSessionBusy(true);
    setSessionNotice(null);
    try {
      await apiFetch("/account/sessions/revoke-others", { method: "POST" });
      setSessionNotice({ tone: "success", text: "其他设备的登录状态已清除。" });
    } catch (error) {
      setSessionNotice({ tone: "error", text: messageFor(error) });
    } finally {
      setSessionBusy(false);
    }
  };

  const saveStudyReminder = async (event: FormEvent) => {
    event.preventDefault();
    setReminderBusy(true);
    setReminderNotice(null);
    try {
      const saved = await apiFetch<StudyReminderPreference>("/account/study-reminder", {
        method: "PUT",
        body: JSON.stringify({ enabled: reminderEnabled, time: reminderTime }),
      });
      setReminderEnabled(saved.enabled);
      setReminderTime(saved.time);
      setReminderLastSentAt(saved.lastSentAt);
      setReminderNotice({ tone: "success", text: saved.enabled ? `每天 ${saved.time} 的学习提醒已开启。` : "学习提醒已关闭。" });
    } catch (error) {
      setReminderNotice({ tone: "error", text: messageFor(error) });
    } finally {
      setReminderBusy(false);
    }
  };

  return (
    <div className="page profile-page">
      <PageHeader title="个人中心" description="管理公开资料、学习提醒、登录邮箱与账户安全。" />

      <section className="profile-overview" aria-labelledby="profile-overview-title">
        <div className="profile-avatar profile-avatar--large" aria-hidden="true">
          {user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : <UserRound />}
        </div>
        <div className="profile-overview__copy">
          <h2 id="profile-overview-title">{user.displayName ?? "学习者"}</h2>
          <p>{user.email}</p>
          <div className="profile-avatar-actions">
            <input
              ref={avatarInputRef}
              className="sr-only"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={uploadAvatar}
            />
            <Button type="button" onClick={() => avatarInputRef.current?.click()} disabled={avatarBusy}>
              <Camera /> {avatarBusy ? "正在处理…" : "更换头像"}
            </Button>
            {user.avatarUrl ? (
              <Button variant="ghost" type="button" onClick={() => void removeAvatar()} disabled={avatarBusy}>
                <Trash2 /> 移除
              </Button>
            ) : null}
          </div>
          <StatusMessage notice={avatarNotice} />
        </div>
      </section>

      <div className="profile-settings">
        <section className="profile-section" aria-labelledby="profile-name-title">
          <header>
            <UserRound />
            <div><h2 id="profile-name-title">个人资料</h2><p>昵称会显示在学习概览和账户区域。</p></div>
          </header>
          <form onSubmit={saveProfile}>
            <Field label="昵称" htmlFor="profile-display-name" hint="1–80 个字符。">
              <Input id="profile-display-name" required maxLength={80} autoComplete="nickname" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
            </Field>
            <StatusMessage notice={profileNotice} />
            <div className="profile-form-actions">
              <Button variant="primary" type="submit" disabled={profileBusy}>{profileBusy ? "正在保存…" : <><Check /> 保存昵称</>}</Button>
            </div>
          </form>
        </section>

        <section className="profile-section" aria-labelledby="profile-email-title">
          <header>
            <Mail />
            <div><h2 id="profile-email-title">登录邮箱</h2><p>当前邮箱：{user.email}</p></div>
          </header>
          {emailStep === "request" ? (
            <form onSubmit={requestEmailChange}>
              <Field label="新邮箱" htmlFor="profile-new-email">
                <Input id="profile-new-email" type="email" required autoComplete="email" value={newEmail} onChange={(event) => setNewEmail(event.target.value)} placeholder="new@example.com" />
              </Field>
              <Field label="当前密码" htmlFor="profile-email-password" hint="用于确认是你本人操作。">
                <Input id="profile-email-password" type="password" required autoComplete="current-password" value={emailPassword} onChange={(event) => setEmailPassword(event.target.value)} />
              </Field>
              <StatusMessage notice={emailNotice} />
              <div className="profile-form-actions">
                <Button variant="primary" type="submit" disabled={emailBusy}>{emailBusy ? "正在发送…" : "发送验证码"}</Button>
              </div>
            </form>
          ) : (
            <form onSubmit={confirmEmailChange}>
              <Field label="新邮箱" htmlFor="profile-email-target">
                <Input id="profile-email-target" value={newEmail} readOnly />
              </Field>
              <Field label="邮件验证码" htmlFor="profile-email-code">
                <Input id="profile-email-code" required maxLength={8} autoComplete="one-time-code" value={emailCode} onChange={(event) => setEmailCode(event.target.value.toUpperCase())} placeholder="输入验证码" />
              </Field>
              <StatusMessage notice={emailNotice} />
              <div className="profile-form-actions profile-form-actions--split">
                <Button variant="ghost" type="button" onClick={() => { setEmailStep("request"); setEmailCode(""); setEmailNotice(null); }}>修改新邮箱</Button>
                <Button variant="primary" type="submit" disabled={emailBusy}>{emailBusy ? "正在确认…" : "确认更换"}</Button>
              </div>
            </form>
          )}
        </section>

        <section className="profile-section" aria-labelledby="profile-reminder-title">
          <header>
            <BellRing />
            <div><h2 id="profile-reminder-title">学习提醒</h2><p>提醒邮件发送至 {user.email}</p></div>
          </header>
          <form className="profile-reminder-form" onSubmit={saveStudyReminder}>
            {reminderQuery.isLoading ? <LoadingLine /> : (
              <>
                <label className="reminder-toggle">
                  <input type="checkbox" checked={reminderEnabled} onChange={(event) => setReminderEnabled(event.target.checked)} />
                  <span className="reminder-toggle__control" aria-hidden="true"><span /></span>
                  <span><strong>邮件提醒</strong><small>当天没有背词记录时发送</small></span>
                </label>
                <Field label="提醒时间" htmlFor="profile-reminder-time" hint="北京时间，每天检查一次。">
                  <Input id="profile-reminder-time" type="time" step={60} required disabled={!reminderEnabled} value={reminderTime} onChange={(event) => setReminderTime(event.target.value)} />
                </Field>
                {reminderLastSentAt ? <p className="reminder-last-sent">最近发送：{new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Shanghai" }).format(new Date(reminderLastSentAt))}</p> : null}
              </>
            )}
            <StatusMessage notice={reminderQuery.isError ? { tone: "error", text: "暂时无法读取提醒设置。" } : reminderNotice} />
            <div className="profile-form-actions">
              <Button variant="primary" type="submit" disabled={reminderQuery.isLoading || reminderQuery.isError || reminderBusy}>{reminderBusy ? "正在保存…" : <><Check /> 保存提醒</>}</Button>
            </div>
          </form>
        </section>

        <section className="profile-section" aria-labelledby="profile-password-title">
          <header>
            <KeyRound />
            <div><h2 id="profile-password-title">修改密码</h2><p>更新后会自动退出其他设备。</p></div>
          </header>
          <form onSubmit={changePassword}>
            <Field label="当前密码" htmlFor="profile-current-password">
              <Input id="profile-current-password" type="password" required autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
            </Field>
            <Field label="新密码" htmlFor="profile-next-password" hint="至少 10 位，建议使用长密码。">
              <Input id="profile-next-password" type="password" minLength={10} required autoComplete="new-password" value={nextPassword} onChange={(event) => setNextPassword(event.target.value)} />
            </Field>
            <Field label="确认新密码" htmlFor="profile-confirm-password">
              <Input id="profile-confirm-password" type="password" minLength={10} required autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
            </Field>
            <StatusMessage notice={passwordNotice} />
            <div className="profile-form-actions">
              <Button variant="primary" type="submit" disabled={passwordBusy}>{passwordBusy ? "正在更新…" : "更新密码"}</Button>
            </div>
          </form>
        </section>

        <section className="profile-section profile-section--action" aria-labelledby="profile-sessions-title">
          <header>
            <MonitorSmartphone />
            <div><h2 id="profile-sessions-title">登录设备</h2><p>保留当前设备，并退出其他浏览器和设备。</p></div>
          </header>
          <div>
            <Button type="button" onClick={() => void revokeOtherSessions()} disabled={sessionBusy}>
              {sessionBusy ? "正在处理…" : "退出其他设备"}
            </Button>
            <StatusMessage notice={sessionNotice} />
          </div>
        </section>
      </div>
    </div>
  );
}
